import React, { useRef, useEffect } from 'react';
import { FiSend, FiMic, FiMicOff } from 'react-icons/fi';
import NeonButton from '../Common/Button';
import { getQuestionsForPhase } from '../../services/phaseQuestions';
import './Chat.css';
import { useLanguage } from '../../context/LanguageContext';
import useVoiceInput from '../../hooks/useVoiceInput';
import VoiceStatus from '../Common/VoiceStatus';

const ChatInput = ({
  inputText,
  onInputChange,
  onSend,
  onQuickQuestion,
  currentPhase,
  disabled = false
}) => {
  const { language, t } = useLanguage();
  const textareaRef = useRef(null);
  const voice = useVoiceInput({ value: inputText, onChange: onInputChange, language, enabled: !disabled });
  const { isListening } = voice;
  const handleSend = (event) => {
    event.preventDefault();
    if (disabled) return;
    voice.cancel();
    onSend(event);
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [inputText]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const quickQuestions = currentPhase === 'Anamnesis'
    ? getQuestionsForPhase(currentPhase, language)
    : [];

  return (
    <div className="input-area">
      <form className="chat-input" onSubmit={handleSend}>
        {quickQuestions.length > 0 && (
          <div className="quick-questions">
            <span className="quick-label">{t('suggestedPrompts')}</span>
            <div className="quick-buttons">
              {quickQuestions.slice(0, 4).map((q, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="quick-btn"
                  disabled={disabled}
                  onClick={() => { voice.cancel(); onQuickQuestion(q); }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="input-wrapper">
          <textarea
            ref={textareaRef}
            value={inputText}
            disabled={disabled}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isListening ? t('listening') : (
              currentPhase === 'Anamnesis'
                ? t('askPatient')
                : currentPhase === 'Physical Exam'
                  ? t('requestExam')
                  : t('clinicalQuestion')
            )}
            className={`message-input ${isListening ? 'listening' : ''}`}
            rows="1"
          />
          <div className="input-actions">
            <button
              type="button"
              className={`action-btn mic-btn ${isListening ? 'listening' : ''}`}
              onClick={voice.toggle}
              disabled={disabled || voice.isProcessing}
              aria-label={isListening ? t('stopListening') : t('startVoice')}
              aria-pressed={isListening}
              title={isListening ? t('stopListening') : t('startVoice')}
            >
              {isListening ? <FiMicOff size={20} /> : <FiMic size={20} />}
            </button>
            <NeonButton
              type="submit"
              variant="primary"
              disabled={disabled || !inputText.trim()}
              className="send-btn"
            >
              <FiSend size={20} />
              <span>{t('send')}</span>
            </NeonButton>
          </div>
        </div>
        <VoiceStatus voice={voice} />
      </form>
    </div>
  );
};

export default ChatInput;
